// OfflineSync-Service für Fahrten (MVP)
import { openDB } from 'idb'

const DB_NAME = 'ragacars-offline'
const STORE_NAME = 'pendingActions'
const LOCATIONS_STORE = 'locationsCache'
const TRANSPORT_ID_MAP = 'transportIdMap'
const TRACKING_CACHE = 'trackingCache'

export type OfflineAction = {
  id: string // UUID
  type: 'startDrive' | 'endDrive'
  payload: any
  createdAt: number
}

// IndexedDB initialisieren (erweitert um Locations-Store)
async function getDb() {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(LOCATIONS_STORE)) {
        db.createObjectStore(LOCATIONS_STORE, { keyPath: 'airportId' })
      }
      if (!db.objectStoreNames.contains(TRANSPORT_ID_MAP)) {
        db.createObjectStore(TRANSPORT_ID_MAP, { keyPath: 'offlineId' })
      }
      if (!db.objectStoreNames.contains(TRACKING_CACHE)) {
        db.createObjectStore(TRACKING_CACHE, { keyPath: 'offlineId' })
      }
    }
  })
}

// DEV-228: Beim Nachspielen ist kein Fahrer mehr da, der einen beanstandeten
// Kilometerstand bestaetigen koennte. Ein 409 wuerde die Aktion in der
// Warteschlange lassen und bei jedem Sync erneut scheitern, die Fahrt also nie
// abschliessen. Der Wert wurde beim Beenden bereits eingegeben; auffaellige
// Faelle fallen im woechentlichen Abrechnungs-Review auf.
function withMileageConfirmed(data: any): any {
  if (data instanceof FormData) {
    data.set('mileage_confirmed', '1')

    return data
  }

  return { ...data, mileage_confirmed: true }
}

export async function saveOfflineAction(action: OfflineAction) {
  const db = await getDb()
  await db.put(STORE_NAME, action)
}

export async function getAllOfflineActions(): Promise<OfflineAction[]> {
  const db = await getDb()
  return await db.getAll(STORE_NAME)
}

export async function removeOfflineAction(id: string) {
  const db = await getDb()
  await db.delete(STORE_NAME, id)
}

export async function saveLocationsToCache(airportId: number, locations: any[]) {
  const db = await getDb()
  await db.put(LOCATIONS_STORE, { airportId: String(airportId), locations })
}

export async function getLocationsFromCache(airportId: number): Promise<any[] | null> {
  const db = await getDb()
  const entry = await db.get(LOCATIONS_STORE, String(airportId))
  return entry ? entry.locations : null
}

// Hilfsfunktion: Standorte für User-Objekt cachen
export async function cacheLocationsForUser(user: any, api: any) {
  if (!user || !user.airport_id) return
  try {
    const response = await api.get(`/api/airports/${user.airport_id}`)
    console.log('[DEBUG] API-Response für airport:', response.data)
    if (response.data.data && response.data.data.locations && Array.isArray(response.data.data.locations)) {
      await saveLocationsToCache(user.airport_id, response.data.data.locations)
      console.log('[DEBUG] Standorte global gecacht für airport_id', user.airport_id)
    } else {
      console.warn('[DEBUG] API liefert keine Locations für airport_id', user.airport_id, response.data)
    }
  } catch (e) {
    console.error('[DEBUG] Fehler beim globalen Caching der Standorte:', e)
  }
}

async function saveTransportIdMapping(offlineId: string, realId: number) {
  const db = await getDb()
  await db.put(TRANSPORT_ID_MAP, { offlineId, realId })
}

async function getRealTransportId(offlineId: string): Promise<number | null> {
  const db = await getDb()
  const entry = await db.get(TRANSPORT_ID_MAP, offlineId)
  return entry ? entry.realId : null
}

export async function saveTrackingPoint(offlineId: string, point: any) {
  const db = await getDb()
  const entry = await db.get(TRACKING_CACHE, offlineId)
  let points = entry ? entry.points : []
  points.push(point)
  await db.put(TRACKING_CACHE, { offlineId, points })
}

async function getTrackingPoints(offlineId: string): Promise<any[]> {
  const db = await getDb()
  const entry = await db.get(TRACKING_CACHE, offlineId)
  return entry ? entry.points : []
}

async function removeTrackingPoints(offlineId: string) {
  const db = await getDb()
  await db.delete(TRACKING_CACHE, offlineId)
}

// Synchronisation: Versuche alle Aktionen an die API zu senden
export async function syncOfflineActions(api: any) {
  const actions = await getAllOfflineActions()
  // Zuerst alle startDrive-Aktionen synchronisieren (und Mappings anlegen)
  for (const action of actions) {
    try {
      if (action.type === 'startDrive') {
        // Entferne die Prüfung auf alreadyMapped
        let payload = { ...action.payload }
        if (!payload.vehicle_id) {
          const searchRes = await api.post('/api/vehicles/search', { license_plate: payload.license_plate })
          if (searchRes.data && searchRes.data.data && searchRes.data.data.length > 0) {
            payload.vehicle_id = searchRes.data.data[0].id
          } else {
            console.warn('Kein Fahrzeug für Kennzeichen gefunden:', payload.license_plate)
            continue
          }
        }
        const response = await api.post('/api/driver/transports/start', payload)
        if (response.data && response.data.data && response.data.data.id) {
          await saveTransportIdMapping(action.id, response.data.data.id)
          // Trackingpunkte synchronisieren
          const trackingPoints = await getTrackingPoints(action.id)
          for (const point of trackingPoints) {
            await api.post(`/api/driver/transports/${response.data.data.id}/track`, point)
          }
          await removeTrackingPoints(action.id)
          // Prüfe, ob es eine endDrive-Aktion für diese Fahrt gibt und führe sie direkt aus
          const allActions = await getAllOfflineActions()
          const endAction = allActions.find(a => a.type === 'endDrive' && a.payload.id === action.id)
          if (endAction) {
            await api.post(`/api/driver/transports/${response.data.data.id}/complete`, withMileageConfirmed(endAction.payload.data))
            await removeOfflineAction(endAction.id)
          }
        }
        await removeOfflineAction(action.id)
      }
    } catch (e) {
      console.error('Sync-Fehler für Offline-Aktion:', action, e)
    }
  }
  // Dann alle endDrive-Aktionen synchronisieren (jetzt existiert Mapping)
  const remaining = await getAllOfflineActions()
  for (const action of remaining) {
    try {
      if (action.type === 'endDrive') {
        let realId = action.payload.id
        if (typeof realId === 'string' && isNaN(Number(realId))) {
          const mapped = await getRealTransportId(realId)
          if (mapped) {
            realId = mapped
          } else {
            console.warn('Kein Mapping für Offline-Transport-ID gefunden:', realId)
            continue
          }
        }
        await api.post(`/api/driver/transports/${realId}/complete`, withMileageConfirmed(action.payload.data))
        await removeOfflineAction(action.id)
      }
    } catch (e) {
      console.error('Sync-Fehler für Offline-Aktion:', action, e)
    }
  }
} 